/**
 * Global decorative effects ported 1:1 from the design:
 *  - custom cursor (dot + ring) with hover-grow on interactive elements
 *  - reveal-on-scroll via IntersectionObserver
 *
 * The noise overlay is pure CSS (see _effects.scss).
 */

function initCursor() {
  // Only on devices with a fine pointer (desktop). CSS hides the markup
  // below 1024px, but we also skip the listeners to save work.
  if (!window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches) {
    return;
  }

  const cur = document.createElement('div');
  cur.className = 'cursor';
  const ring = document.createElement('div');
  ring.className = 'cursor-ring';
  document.body.append(cur, ring);

  document.addEventListener('mousemove', (e) => {
    cur.style.left = `${e.clientX}px`;
    cur.style.top = `${e.clientY}px`;
    ring.style.left = `${e.clientX}px`;
    ring.style.top = `${e.clientY}px`;
  });

  const grow = () => {
    cur.style.width = '20px';
    cur.style.height = '20px';
    ring.style.width = '52px';
    ring.style.height = '52px';
  };
  const shrink = () => {
    cur.style.width = '12px';
    cur.style.height = '12px';
    ring.style.width = '36px';
    ring.style.height = '36px';
  };

  const hoverSelector =
    'a, button, .svc-card, .tool-card, .blog-card, .review-card, ' +
    '.filter-tag, .filter-btn, .budget-pill, .page-btn, .proj-card, ' +
    '.contact-item, .tech-badge, .sidebar-btn';

  document.querySelectorAll(hoverSelector).forEach((el) => {
    el.addEventListener('mouseenter', grow);
    el.addEventListener('mouseleave', shrink);
  });
}

/**
 * Сторінка послуги: вішаємо .reveal на картки/кроки/FAQ через JS (а не в
 * розмітці), щоб без JS контент лишався видимим. Колонкові сітки з'їжджають
 * зліва/справа по черзі, решта — знизу з невеликим stagger.
 * Після появи класи й delay знімаються (див. cleanupReveal), щоб не впливати на hover.
 */
function initServiceMotion() {
  const root = document.querySelector(
    '.svc-section, .svc-hero, .svc-cta, .services, .about, .process-section, .portfolio'
  ) ? document : null;
  if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const groups = [
    ['.svc-text-head, .svc-text-body', 'up'],
    ['.services-header', 'up'],
    ['.services-grid .svc-card', 'rows'],
    ['.svc-section > .svc-eyebrow, .svc-section > h2, .svc-sub', 'up'],
    ['.svc-why-list .svc-why-item', 'grid'],
    ['.svc-steps .svc-step', 'steps'],
    ['.svc-faq .wp-block-details', 'faq'],
    // лише портфоліо; блок «Блог» на головній без скрол-анімації
    ['.portfolio .blog-header, .portfolio .blog-grid .blog-card', 'up'],
    ['.svc-cta-box', 'up'],
    // головна: ті самі ефекти для блоків із дизайну
    ['.about-visual', 'left'],
    ['.about-content', 'right'],
    ['.process-eyebrow, .process-title', 'up'],
    ['.process-cards .process-card', 'stagger'],
  ];

  /**
   * Напрямок за реальною колонкою сітки: ліва — зліва, права — справа,
   * центральна (при 3+ колонках) стоїть без анімації. 1 колонка — знизу.
   */
  const gridDirection = (el, i) => {
    const cols = getComputedStyle(el.parentElement).gridTemplateColumns.split(' ').length;
    if (cols < 2) return 'up';
    const col = i % cols;
    if (col === 0) return 'left';
    if (col === cols - 1) return 'right';
    return null;
  };

  // колонок у сітці (за реальним grid-template-columns)
  const columnsOf = (el) => getComputedStyle(el.parentElement).gridTemplateColumns.split(' ').length;

  groups.forEach(([selector, mode]) => {
    root.querySelectorAll(selector).forEach((el, i) => {
      // «Інші послуги»: перший рядок видно одразу, наступні легко з'їжджають знизу
      if (mode === 'rows') {
        const cols = columnsOf(el);
        if (Math.floor(i / cols) === 0) return;
        el.classList.add('reveal', 'reveal--up');
        el.dataset.revealOnce = '1';
        el.style.transitionDelay = `${(i % cols) * 90}ms`;
        return;
      }

      // дві колонки (about): зліва/справа на desktop, на мобільному — знизу (без бічного зсуву)
      if (mode === 'left' || mode === 'right') {
        const dirSide = window.matchMedia('(min-width: 1024px)').matches ? mode : 'up';
        el.classList.add('reveal', `reveal--${dirSide}`);
        el.dataset.revealOnce = '1';
        return;
      }
      // картки процесу: усі виїжджають знизу по черзі
      if (mode === 'stagger') {
        el.classList.add('reveal', 'reveal--up');
        el.dataset.revealOnce = '1';
        el.style.transitionDelay = `${i * 120}ms`;
        return;
      }

      // кроки: перший видно одразу, решта виїжджають знизу по черзі (до ~1 с)
      if (mode === 'steps' && i === 0) return;
      let dir = 'up';
      if (mode === 'grid') dir = gridDirection(el, i);
      // FAQ у двох колонках (CSS columns): перша половина зліва, друга справа
      if (mode === 'faq') {
        const colCount = parseInt(getComputedStyle(el.parentElement).columnCount, 10) || 1;
        const total = el.parentElement.children.length;
        if (colCount > 1) dir = i < Math.ceil(total / colCount) ? 'left' : 'right';
      }
      if (!dir) return;
      el.classList.add('reveal', `reveal--${dir}`);
      el.dataset.revealOnce = '1';
      // stagger: до 4 кроків, щоб довгі списки не «розтягувались» у часі;
      // кроки — повільніше (250 мс між ними → 5-й через 1 с)
      el.style.transitionDelay = `${mode === 'steps' ? i * 250 : Math.min(i, 4) * 90}ms`;
    });
  });
}

function cleanupReveal(el) {
  el.classList.remove('reveal', 'reveal--left', 'reveal--right', 'reveal--up', 'visible');
  el.style.transitionDelay = '';
  delete el.dataset.revealOnce;
}

function initReveal() {
  const items = document.querySelectorAll('.reveal');
  if (!items.length) return;

  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.classList.add('visible');
          observer.unobserve(el);
          // прибираємо класи вже ПІСЛЯ завершення переходу (delay + 0.6 с + запас)
          if (el.dataset.revealOnce) {
            setTimeout(() => cleanupReveal(el), 900 + (parseFloat(el.style.transitionDelay) || 0));
          }
        }
      });
    },
    { threshold: 0.08 }
  );

  items.forEach((el) => observer.observe(el));
}

export default function effects() {
  initCursor();
  initServiceMotion();
  initReveal();
}
